import { useEffect, useId, useRef, useState } from 'react';
import type { FormEvent, MouseEvent } from 'react';

import {
  Alert,
  Button,
  Card,
  Field,
  Heading,
  IconButton,
  Inline,
  Input,
  Spinner,
  Stack,
  Text,
  cx,
} from '../design/index.js';
import { createInvite } from '../api.js';
import type { InviteSummary } from '../api.js';
import styles from './ShareDialog.module.css';

export interface ShareDialogProps {
  /** The caller's own nodes, to choose from. */
  nodes: { id: string; title: string }[];
  onClose: () => void;
  /** Fired after a successful invitation, so the caller can refresh. */
  onSent?: (invite: InviteSummary) => void;
  className?: string;
}

type Phase =
  | { kind: 'editing' }
  | { kind: 'submitting' }
  | { kind: 'sent'; invite: InviteSummary; link: string };

type CopyState = 'idle' | 'copied' | 'manual';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Copies `text` to the clipboard where the API is available and permitted,
 * and otherwise selects it in `input` so the person can copy it themself
 * with their own keyboard shortcut. Returns which happened — a failed copy
 * must never read as a failed invitation, so the caller only ever uses this
 * to word the hint under the link, never to raise an error.
 */
async function copyLink(text: string, input: HTMLInputElement | null): Promise<CopyState> {
  try {
    if (!navigator.clipboard?.writeText) throw new Error('Clipboard API unavailable');
    await navigator.clipboard.writeText(text);
    return 'copied';
  } catch {
    input?.select();
    return 'manual';
  }
}

/**
 * The two-step "share specific nodes with someone" panel: an address, then
 * which of the owner's own nodes they'd see. Nothing is preselected —
 * defaulting to "everything" is exactly the mistake this feature exists to
 * replace — and submit stays disabled until both are filled in.
 *
 * The success state never says whether the address already has a log.gy
 * account: the server deliberately withholds that (see the wave's shared
 * header), so the copy here covers both outcomes at once and always shows
 * the link, which works either way.
 */
export function ShareDialog({ nodes, onClose, onSent, className }: ShareDialogProps) {
  const headingId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const linkInputRef = useRef<HTMLInputElement | null>(null);
  const copyResetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [email, setEmail] = useState('');
  const [selectedNodeIds, setSelectedNodeIds] = useState<ReadonlySet<string>>(() => new Set());
  const [phase, setPhase] = useState<Phase>({ kind: 'editing' });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<CopyState>('idle');

  /**
   * What `role="dialog"` + `aria-modal` actually promise.
   *
   * Claiming them without doing this is worse than not claiming them: a
   * screen reader is told the rest of the page is inert while Tab walks
   * straight out of the panel into the dashboard behind it. So: focus moves
   * in on open, Tab and Shift+Tab cycle within the panel, and focus returns
   * to whatever opened the dialog on close — otherwise the reader is
   * dropped at the top of the document with no idea where they were.
   */
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    // Prefer the first field over the panel itself: landing on the email
    // input is where the person is going anyway.
    const firstField = panel?.querySelector<HTMLElement>('input, button, select, textarea');
    (firstField ?? panel)?.focus();

    return () => {
      // `isConnected` guards the case where the opener has itself been
      // unmounted while the dialog was up; focusing a detached node silently
      // moves focus to <body>, which is the very thing this avoids.
      if (opener?.isConnected) opener.focus();
    };
  }, []);

  useEffect(() => {
    function trapTab(event: KeyboardEvent) {
      if (event.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = [
        ...panel.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ].filter((el) => el.offsetParent !== null || el === document.activeElement);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', trapTab, true);
    return () => document.removeEventListener('keydown', trapTab, true);
  }, []);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    return () => {
      if (copyResetTimer.current) clearTimeout(copyResetTimer.current);
    };
  }, []);

  const busy = phase.kind === 'submitting';
  const trimmedEmail = email.trim();
  const canSubmit = EMAIL_PATTERN.test(trimmedEmail) && selectedNodeIds.size > 0 && !busy;

  function toggleNode(nodeId: string) {
    if (busy) return;
    setSelectedNodeIds((current) => {
      const next = new Set(current);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSubmit) return;

    setPhase({ kind: 'submitting' });
    setSubmitError(null);
    try {
      const { invite, link } = await createInvite({
        email: trimmedEmail,
        nodeIds: [...selectedNodeIds],
      });
      setPhase({ kind: 'sent', invite, link });
      onSent?.(invite);
    } catch (err) {
      setPhase({ kind: 'editing' });
      setSubmitError(err instanceof Error ? err.message : 'Could not send this invite.');
    }
  }

  async function handleCopy(link: string) {
    if (copyResetTimer.current) clearTimeout(copyResetTimer.current);
    const result = await copyLink(link, linkInputRef.current);
    setCopyState(result);
    copyResetTimer.current = setTimeout(() => setCopyState('idle'), 2500);
  }

  function handleBackdropMouseDown(event: MouseEvent<HTMLDivElement>) {
    if (event.target === event.currentTarget) onClose();
  }

  const closeButton = (
    <IconButton aria-label="Close" onClick={onClose} data-testid="share-dialog-close">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M6 6l12 12M18 6L6 18" />
      </svg>
    </IconButton>
  );

  if (nodes.length === 0) {
    return (
      <div className={cx(styles.overlay, className)} onMouseDown={handleBackdropMouseDown}>
        <Card
          ref={panelRef}
          tabIndex={-1}
          variant="raised"
          className={styles.panel}
          role="dialog"
          aria-modal="true"
          aria-labelledby={headingId}
          data-testid="share-dialog-empty"
        >
          <Inline align="center" justify="between">
            <Heading id={headingId} level={3} size="lg">Nothing to share yet</Heading>
            {closeButton}
          </Inline>
          <Text size="sm" color="secondary">
            Sharing hands someone a specific node to look at, and there's nothing in your orbit
            yet. Add something to track first, then come back here to share it.
          </Text>
        </Card>
      </div>
    );
  }

  return (
    <div className={cx(styles.overlay, className)} onMouseDown={handleBackdropMouseDown}>
      <Card
        ref={panelRef}
        tabIndex={-1}
        variant="raised"
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        data-testid="share-dialog"
      >
        <Inline align="center" justify="between">
          <Heading id={headingId} level={3} size="lg">Share with someone</Heading>
          {closeButton}
        </Inline>

        {phase.kind === 'sent' ? (
          <Stack gap={5} data-testid="share-dialog-sent">
            <Alert variant="success" title="Invite ready">
              They&rsquo;ll see this in their notifications if they already use log.gy.
              Otherwise, send them this link.
            </Alert>

            <Field label="Link to share">
              <Inline gap={2} align="center" wrap>
                <Input
                  ref={linkInputRef}
                  value={phase.link}
                  readOnly
                  data-testid="share-dialog-link"
                  onFocus={(event) => event.currentTarget.select()}
                  className={styles.linkInput}
                />
                <Button
                  type="button"
                  variant="secondary"
                  data-testid="share-dialog-copy"
                  onClick={() => void handleCopy(phase.link)}
                >
                  {copyState === 'copied' ? 'Copied' : 'Copy link'}
                </Button>
              </Inline>
            </Field>
            {copyState === 'manual' && (
              <Text size="xs" color="muted">
                Couldn&rsquo;t copy that automatically — it&rsquo;s selected, so your own
                copy shortcut will grab it.
              </Text>
            )}

            <Inline gap={3} align="center" wrap>
              <Button type="button" onClick={onClose} data-testid="share-dialog-done">
                Done
              </Button>
            </Inline>
          </Stack>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <Stack gap={5}>
              <Field label="Their email" required>
                <Input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="friend@example.com"
                  disabled={busy}
                  data-testid="share-dialog-email"
                  required
                />
              </Field>

              <fieldset className={styles.fieldset} disabled={busy}>
                <legend className={styles.legend}>
                  <Text size="sm" weight="medium">What can they see?</Text>
                </legend>
                <Stack gap={2}>
                  {nodes.map((node) => {
                    const checked = selectedNodeIds.has(node.id);
                    return (
                      <label key={node.id} className={styles.checkboxRow}>
                        <input
                          type="checkbox"
                          className={styles.checkbox}
                          checked={checked}
                          onChange={() => toggleNode(node.id)}
                          data-testid={`share-dialog-node-${node.id}`}
                        />
                        <Text size="sm">{node.title}</Text>
                      </label>
                    );
                  })}
                </Stack>
              </fieldset>

              {submitError && (
                <Alert variant="error" title="Couldn't send this invite" data-testid="share-dialog-error">
                  {submitError}
                </Alert>
              )}

              <Inline gap={3} align="center" wrap>
                <Button type="submit" disabled={!canSubmit} data-testid="share-dialog-submit">
                  {busy ? 'Sending…' : 'Send invite'}
                </Button>
                {busy && <Spinner size="sm" aria-label="Sending this invite" />}
                <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
                  Cancel
                </Button>
              </Inline>
            </Stack>
          </form>
        )}
      </Card>
    </div>
  );
}
